<template>
  <div class="token-manager">

    <!-- Token Generation Button -->
    <div class="d-flex gap-2 mb-4">
      <button 
        class="btn btn-success btn-sm" 
        @click="generateToken"
        :disabled="generating"
      >
        <i class="bi bi-plus-circle"></i>
        {{ $t('tokens.generate_token') }}
      </button>
    </div>

    <!-- Personal Access Tokens Section -->
    <div class="mb-4">
      <h6>{{ $t('tokens.personal_access_tokens') }}</h6>
      <div v-if="tokens.length > 0" class="table-responsive">
        <table class="table table-sm">
          <thead>
            <tr>
              <th>{{ $t('tokens.name') }}</th>
              <th>{{ $t('tokens.token_value') }}</th>
              <th>{{ $t('tokens.abilities') }}</th>
              <th>{{ $t('tokens.expires_at') }}</th>
              <th>{{ $t('tokens.created_at') }}</th>
              <th>{{ $t('tokens.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="token in tokens" :key="token.id">
              <td>{{ token.name }}</td>
              <td>
                <code v-if="token.plain_text_token" class="text-truncate d-inline-block" style="max-width: 150px;">
                  {{ token.plain_text_token }}
                </code>
                <span v-else class="text-muted">{{ $t('tokens.token_not_shown_after_creation') }}</span>
                <button 
                  v-if="token.plain_text_token"
                  class="btn btn-link btn-sm p-0 ms-1"
                  @click="copyToClipboard(token.plain_text_token)"
                  :title="$t('tokens.copy_token')"
                >
                  <i class="bi bi-clipboard"></i>
                </button>
              </td>
              <td>
                <small class="text-muted">
                  {{ token.abilities ? token.abilities.join(', ') : '*' }}
                </small>
              </td>
              <td>{{ token.expires_at ? formatDateTime(token.expires_at) : $t('tokens.never_expires') }}</td>
              <td>{{ formatDateTime(token.created_at) }}</td>
              <td>
                <button 
                  class="btn btn-outline-danger btn-sm"
                  @click="deleteToken(token)"
                  :disabled="updating"
                >
                  {{ $t('tokens.delete') }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p v-else class="text-muted">{{ $t('tokens.no_tokens') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  getPersonalAccessTokens,
  generatePersonalAccessToken,
  deletePersonalAccessToken
} from '../api/admin/TokenApi';
import useFlashMessage from '../composables/useFlashMessage';
import { formatDateTime } from '../utils/DateTimeUtil';

const props = defineProps({
  dataProviderId: {
    type: String,
    required: true
  }
});

const { t } = useI18n();
const { successMessage, errorMessage } = useFlashMessage();

const tokens = ref([]);
const generating = ref(false);
const updating = ref(false);

/**
 * Load tokens
 */
const loadTokens = async () => {
  try {
    tokens.value = await getPersonalAccessTokens(props.dataProviderId);
  } catch (e) {
    errorMessage(e.message);
  }
};

/**
 * Generate personal access token
 */
const generateToken = async () => {
  const name = prompt(t('tokens.messages.enter_token_name'));
  if (!name) return;
  
  generating.value = true;
  try {
    const newToken = await generatePersonalAccessToken(props.dataProviderId, name);
    successMessage(t('tokens.messages.token_generated'));
    
    // Add the new token to the beginning of the list with plain_text_token
    tokens.value.unshift(newToken);
  } catch (e) {
    errorMessage(e.message);
  } finally {
    generating.value = false;
  }
};

/**
 * Delete personal access token
 */
const deleteToken = async (token) => {
  if (confirm(t('tokens.messages.confirm_delete_token'))) {
    updating.value = true;
    try {
      await deletePersonalAccessToken(props.dataProviderId, token.id);
      successMessage(t('tokens.messages.token_deleted'));
      await loadTokens();
    } catch (e) {
      errorMessage(e.message);
    } finally {
      updating.value = false;
    }
  }
};

/**
 * Copy token to clipboard
 */
const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    successMessage(t('tokens.messages.token_copied'));
  } catch (e) {
    errorMessage(t('tokens.messages.copy_failed'));
  }
};



onMounted(() => {
  loadTokens();
});
</script>

<style scoped>
.token-manager {
  padding: 1rem 0;
}

.table-responsive {
  max-height: 300px;
  overflow-y: auto;
}

code {
  font-size: 0.8rem;
}
</style>